import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, FormControl } from '@mui/material';
import CustomFieldWrapper from './CustomFieldWrapper';
import { useDropdownDependencies } from '../../contexts/DropdownDependenciesContext';
import { useMultiAnswerField } from '../../contexts/MultiAnswerFieldContext'; // ← NEW: plain options map fallback
import debounce from 'lodash/debounce';

import { useFormAnswers } from '../../contexts/FormAnswersContext'; // other text channel
import { applyTransformations } from '../../utils/transformationUtils'; // live caps/spacing like TextFieldComponent

const MultiSelectFieldComponent = ({
  fieldId,
  fieldMetadata = {},
  value: propValue = [],
  onChange,
  disabled = false,
  isSubmitting = false,
  isBuilderMode = false,
  setShowDropZones,
  variant,
  error,
  helperText,
  externalHelperText,
}) => {

  const {
    dynamicDropdownOptions,
    notifyDependentFields,
    loadDependenciesForField,   // ← NEW: match SingleSelect per-UFI fetch behavior
    loadingMap,                 // ← NEW: per-UFI loading state
    loading,
    error: fetchError,
  } = useDropdownDependencies();

//  console.log(`[MultiSelectFieldComponent] Render start for fieldId ${fieldId}. Current dynamicDropdownOptions:`, dynamicDropdownOptions);

  const { dropdownOptions } = useMultiAnswerField(); // ← NEW: read the plain options map as a fallback

  // ─────────────────────────────────────────────────────────────
  // "Other" runtime-only behavior (multi-select)
  // - selection stays optionId "__other__" inside the array
  // - typed text stored separately in FormAnswersContext
  // ─────────────────────────────────────────────────────────────
  const OTHER_OPTION_ID = "__other__";
  const ufi = fieldMetadata?.ufi || fieldId;

  const { getOtherText, setOtherText, clearOtherText } = useFormAnswers() || {};
  const otherTextValue = (typeof getOtherText === 'function') ? getOtherText(ufi) : '';

  const allowOtherEnabled =
    fieldMetadata?.allowOther === true || fieldMetadata?.includeOther === true;

  // per-dropdown loading flag
  // IMPORTANT:
  // - We still track dependency loading for correctness
  // - But once the field already has renderable options (seeded local or managed),
  //   we should NOT flash a transient "Loading options..." message in the builder.
  const perUfiLoading =
    fieldMetadata.optionsSetUFI &&
    loadingMap &&
    loadingMap[fieldMetadata.optionsSetUFI] === true;

  const [internalValue, setInternalValue] = useState([]); // Array for multi-select values
  const [options, setOptions] = useState([]);

  // Enterprise-grade render policy:
  // - Only treat the field as "blocking/loading" when we have no renderable options yet
  // - If options already exist, render normally and let dependency refresh happen silently
  const shouldBlockRenderForOptions =
    perUfiLoading && (!Array.isArray(options) || options.length === 0);

  const transformations = useMemo(() => fieldMetadata.transformations || {}, [fieldMetadata.transformations]);
  
  /* ────────────────  make sure this dropdown’s dependencies are fetched  ──────────────── */
  useEffect(() => {
    if (!fieldMetadata.optionsSetUFI) return; // nothing to fetch
    // if this UFI is not currently being fetched *and* we’ve never fetched it, kick it off
    if (loadingMap && loadingMap[fieldMetadata.optionsSetUFI] === undefined) {
      loadDependenciesForField(fieldMetadata.optionsSetUFI);
    }
  }, [fieldMetadata.optionsSetUFI, loadDependenciesForField, loadingMap]);
  /* ─────────────────────────────────────────────────────────────────────────── */

  useEffect(() => {
    console.log(`[MultiSelectFieldComponent: useEffect] Checking dynamicDropdownOptions for fieldId ${fieldId}:`, dynamicDropdownOptions);
    console.log(`[MultiSelectFieldComponent: useEffect] optionsSetUFI for fieldId ${fieldId}: ${fieldMetadata.optionsSetUFI}`);

    const setId = fieldMetadata.optionsSetUFI;

    if (!setId) {
      setOptions([]);
      console.warn(`[MultiSelectFieldComponent: useEffect] No options found or invalid structure for optionsSetUFI ${fieldMetadata.optionsSetUFI}.`);
      return;
    }

    // Fallback chain (aligned with SingleSelect):
    // 1) primary: dependency-resolved choices (may be filtered)
    const fromDeps  = dynamicDropdownOptions?.[setId];
    // 2) fallback: plain canonical options from MultiAnswerFieldContext
    const fromPlain = dropdownOptions?.[setId];
    // 3) last-resort: embedded defaults in metadata (created by getDefaultFieldMetadata)
    const embedded  = Array.isArray(fieldMetadata?.options?.options) ? fieldMetadata.options.options : [];

    const optionsToDisplay =
      (Array.isArray(fromDeps)  ? fromDeps  :
       Array.isArray(fromPlain) ? fromPlain :
                                  embedded);

    // ─────────────────────────────────────────────────────────────
    // Runtime-only: append "Other" (never persisted to dropdownOptions)
    // ─────────────────────────────────────────────────────────────
    let finalOptions = Array.isArray(optionsToDisplay) ? [...optionsToDisplay] : [];
    if (allowOtherEnabled) {
      const alreadyHasOther = finalOptions.some(o => o?.optionId === OTHER_OPTION_ID);
      if (!alreadyHasOther) {
        finalOptions.push({
          optionId: OTHER_OPTION_ID,
          label: "Other",
          value: OTHER_OPTION_ID,
          hint: "",
        });
      }
    }

    console.log(`[MultiSelectFieldComponent: useEffect] Setting options for fieldId ${fieldId} with optionsToDisplay:`, optionsToDisplay);
    setOptions(finalOptions);

  }, [
    fieldMetadata.optionsSetUFI,
    fieldMetadata.options,
    dynamicDropdownOptions,
    dropdownOptions,
    fieldId,
    allowOtherEnabled,
    OTHER_OPTION_ID
  ]);

  useEffect(() => {
    setInternalValue(Array.isArray(propValue) ? propValue : []);
  }, [propValue]);

  const applyTransformationsOnSubmit = useCallback(
    (inputValue) => {
      const { capitalize, spacing, allowSymbols } = transformations;
      let transformedValue = inputValue;

      switch (capitalize) {
        case 'first-letter':
          transformedValue = transformedValue.charAt(0).toUpperCase() + transformedValue.slice(1).toLowerCase();
          break;
        case 'all-caps':
          transformedValue = transformedValue.toUpperCase();
          break;
        case 'no-caps':
          transformedValue = transformedValue.toLowerCase();
          break;
        default:
          break;
      }

      switch (spacing) {
        case 'underscore':
          transformedValue = transformedValue.replace(/\s+/g, '_');
          break;
        case 'hyphen':
          transformedValue = transformedValue.replace(/\s+/g, '-');
          break;
        case 'no-spacing':
          transformedValue = transformedValue.replace(/\s+/g, '');
          break;
        default:
          break;
      }

      if (!allowSymbols) {
        transformedValue = transformedValue.replace(/[^\w\s-]/gi, '');
      }

      return transformedValue;
    },
    [transformations]
  );

  const debouncedOnChange = useCallback(
    debounce((newValue) => {
      onChange({
        ...newValue,
        target: {
          ...newValue.target,
          value: newValue.target.value,
        },
      });
    }, 300),
    [onChange]
  );

  const handleChange = useCallback(
    (event, newValues) => {
      if (!newValues) return;

      const selectedOptionIds = Array.isArray(newValues)
        ? newValues
            .map((option) => (typeof option === 'string' ? option : option?.optionId) || '')
            .filter(Boolean)
        : [];

      setInternalValue(selectedOptionIds);

      console.log(`[MultiSelectFieldComponent: handleChange] Selected optionIds: ${selectedOptionIds} for fieldId: ${fieldId}`);
      console.log(`[MultiSelectFieldComponent: handleChange] Current dynamicDropdownOptions before notifying dependencies:`, dynamicDropdownOptions);

      debouncedOnChange({ target: { value: selectedOptionIds } });

      // If "Other" is no longer selected, clear stale typed other text
      if (!selectedOptionIds.includes(OTHER_OPTION_ID)) {
        if (typeof clearOtherText === 'function') {
          try { clearOtherText(ufi); } catch (_) {}
        }
      }


      // Notify dependent fields based on selected options
//      selectedOptionIds.forEach((optionId) => {
//        if (optionId) {
//          notifyDependentFields(fieldMetadata.optionsSetUFI, optionId);
//        }
//      });

      if (isBuilderMode) {
        setShowDropZones(false);
      }
    },
    [
      debouncedOnChange,
      isBuilderMode,
      setShowDropZones,
      notifyDependentFields,
      fieldMetadata.optionsSetUFI,
      fieldId,
      dynamicDropdownOptions,
      OTHER_OPTION_ID,
      clearOtherText,
      ufi
    ]
  );

  const handleBlur = useCallback(() => {
    if (isBuilderMode) {
      setShowDropZones(true);
    }
  }, [isBuilderMode, setShowDropZones]);

  // Prevent rendering only when this dropdown truly has no renderable options yet.
  // IMPORTANT:
  // - We do NOT show a transient "Loading options..." helper message anymore.
  // - If options already exist, we render immediately for a smoother builder UX.
  if (shouldBlockRenderForOptions) {
    // console.log(`[MultiSelectFieldComponent] Waiting for dropdown options for fieldId ${fieldId}`);
    return null;
  }

  return (
    <FormControl fullWidth disabled={disabled || isSubmitting}>
      <CustomFieldWrapper
        label={fieldMetadata.label || 'Select Options'}
        variant={variant}
        dropdownOptions={options}
        multiple={true} // Enable multi-select
        value={internalValue} // Ensure value remains an array
        onChange={handleChange}
        onBlur={handleBlur}
        required={fieldMetadata.required}
        error={error}
        // Enterprise-grade UX:
        // - Never flash "Loading options..." once the field is already renderable
        // - Preserve the field's real helper text unless there is an actual fetch error
        helperText={fetchError ? externalHelperText : helperText}
        placeholder={fieldMetadata.hint || 'Select options'}
        // Allow already-seeded options to remain interactable.
        // Only disable for submit/explicit disabled states.
        disabled={disabled || isSubmitting}
      />

      {/* ─────────────────────────────────────────────────────────────
          Indented "Other" text input (multi-select)
          - Appears when "Other" is included among selected values
          - Applies caps/spacing while typing (TextFieldComponent-style)
          - Encodes later during submission (FormActionsContext)
         ───────────────────────────────────────────────────────────── */}
      {allowOtherEnabled && Array.isArray(internalValue) && internalValue.includes(OTHER_OPTION_ID) && (
        <Box sx={{ ml: 2, mt: 1 }}>
          <CustomFieldWrapper
            label={fieldMetadata.otherHint || 'Please specify'}
            variant={variant}
            value={otherTextValue || ''}
            onChange={(e) => {
              const raw = e?.target?.value ?? '';
              let next = applyTransformations(raw, fieldMetadata.transformations || {});
              const max = fieldMetadata.otherMaxLength ?? fieldMetadata.maxLength ?? null;
              if (max && Number.isFinite(Number(max)) && next.length > Number(max)) {
                next = next.substring(0, Number(max));
              }
              if (typeof setOtherText === 'function') {
                setOtherText(ufi, next);
              }
            }}
            onBlur={handleBlur}
            // Best-practice: if Other is selected, the specify box is required.
            // We'll enforce this at submit-time in FormActionsContext.
            required={true}
            error={error}
            helperText={fieldMetadata.otherHelperText || ''}
            externalHelperText={fieldMetadata.otherErrorText || externalHelperText}
            placeholder={fieldMetadata.otherHint || 'Please specify'}
            fullWidth
            InputProps={{
              inputProps: {
                maxLength: fieldMetadata.otherMaxLength ?? fieldMetadata.maxLength,
              },
            }}
            disabled={disabled || isSubmitting}
          />
        </Box>
      )}
    </FormControl>
  );

};

MultiSelectFieldComponent.propTypes = {
  fieldId: PropTypes.string.isRequired,
  fieldMetadata: PropTypes.shape({
    label: PropTypes.string,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    externalError: PropTypes.bool,
    externalHelperText: PropTypes.string,
    helperText: PropTypes.string,
    hint: PropTypes.string,
    transformations: PropTypes.object,
    optionsSetUFI: PropTypes.string,
    options: PropTypes.object, // may include embedded defaults: { optionsSetUFI, options: [...] }

    allowOther: PropTypes.bool,
    includeOther: PropTypes.bool,
    otherHint: PropTypes.string,
    otherHelperText: PropTypes.string,
    otherErrorText: PropTypes.string,
    otherMaxLength: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isBuilderMode: PropTypes.bool,
  setShowDropZones: PropTypes.func,
  variant: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  externalHelperText: PropTypes.string,
};


export default MultiSelectFieldComponent;
